import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CategoryChip } from "@/pages/Overview";
import { toast } from "sonner";
import {
  Sparkle,
  MagnifyingGlass,
  Warning,
  EnvelopeOpen,
  Envelope,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CATS = [
  { key: "all", label: "All" },
  { key: "emergency", label: "Emergency" },
  { key: "past_due", label: "Past-Due" },
  { key: "renewal", label: "Renewals" },
  { key: "vendor", label: "Vendors" },
  { key: "coworker", label: "Coworkers" },
  { key: "general", label: "General" },
];

export default function EmailTriage() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/emails", { params: { category: cat, q } });
      setEmails(data);
    } catch { toast.error("Failed to load emails"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const reclassify = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/emails/categorize", {});
      toast.success(`Reclassified ${data.updated} emails`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reclassification failed"); }
    setRunning(false);
  };

  const openEmail = async (em) => {
    try {
      const { data } = await api.get(`/emails/${em.id}`);
      setActive(data);
      setEmails((prev) => prev.map(e => e.id === em.id ? { ...e, read: true } : e));
    } catch { toast.error("Could not open email"); }
  };

  const counts = useMemo(() => {
    const m = { all: emails.length };
    for (const e of emails) m[e.category] = (m[e.category] || 0) + 1;
    return m;
  }, [emails]);

  const filtered = useMemo(() => {
    if (!q.trim()) return emails;
    const s = q.toLowerCase();
    return emails.filter(e =>
      (e.subject || "").toLowerCase().includes(s) ||
      (e.from_addr || "").toLowerCase().includes(s) ||
      (e.snippet || "").toLowerCase().includes(s)
    );
  }, [emails, q]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="triage-page">
      <div className="mb-8 flex flex-wrap items-end gap-4 justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-400">Email Triage</div>
          <h1 className="font-heading text-4xl font-semibold text-stone-900 mt-1">Outlook Inbox (mock)</h1>
          <p className="text-stone-500 mt-1">AI auto-categorizes by urgency. Reclassify any time.</p>
        </div>
        <Button onClick={reclassify} disabled={running} className="bg-forest-600 hover:bg-forest-500 text-white" data-testid="reclassify-btn">
          <Sparkle size={16} className="mr-2" /> {running ? "Running AI…" : "Reclassify with AI"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {CATS.map(c => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            data-testid={`cat-${c.key}`}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
              cat === c.key ? "bg-forest-600 text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search sender, subject, or snippet…"
          className="pl-9 bg-white"
          data-testid="triage-search"
        />
      </div>

      <Card className="border-stone-200 rounded-xl shadow-none bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-stone-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-stone-500">No emails match.</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {filtered.map((e) => (
              <button
                key={e.id}
                onClick={() => openEmail(e)}
                data-testid={`email-row-${e.id}`}
                className={`w-full text-left grid grid-cols-12 gap-3 px-5 py-4 items-center hover:bg-sand-50 transition-colors border-l-4 ${
                  e.read ? "border-transparent" : "border-forest-500"
                }`}
              >
                <div className="col-span-1 flex items-center">
                  {e.read ? <EnvelopeOpen size={18} className="text-stone-400" /> : <Envelope size={18} className="text-forest-600" />}
                  {e.urgency_score >= 80 && <Warning size={16} className="text-terra-500 ml-1" weight="fill" />}
                </div>
                <div className="col-span-3">
                  <div className={`truncate ${e.read ? "font-normal text-stone-700" : "font-semibold text-stone-900"}`}>
                    {e.from_name || e.from_addr}
                  </div>
                  <div className="text-xs text-stone-500 truncate">{e.from_addr}</div>
                </div>
                <div className="col-span-5">
                  <div className={`truncate ${e.read ? "text-stone-700" : "font-medium text-stone-900"}`}>
                    {e.subject}
                  </div>
                  <div className="text-xs text-stone-500 truncate">{e.snippet}</div>
                </div>
                <div className="col-span-2 flex justify-start">
                  <CategoryChip category={e.category} />
                </div>
                <div className="col-span-1 text-right text-xs text-stone-400">
                  {timeAgo(e.received_at)}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="email-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{active?.subject}</DialogTitle>
            <div className="text-sm text-stone-500">
              From {active?.from_name || active?.from_addr} · {active?.received_at && new Date(active.received_at).toLocaleString()}
            </div>
          </DialogHeader>
          <div className="flex items-center gap-2 mt-2">
            <CategoryChip category={active?.category || "general"} />
            <span className="text-xs text-stone-500">Urgency {active?.urgency_score ?? 0}/100</span>
          </div>
          <div className="mt-4 whitespace-pre-wrap text-sm text-stone-700 leading-relaxed max-h-[50vh] overflow-auto" data-testid="email-body">
            {active?.body}
          </div>
          {active?.ai_reason && (
            <div className="mt-3 text-xs text-stone-500 bg-sand-50 border border-stone-200 rounded-md p-3">
              <span className="font-medium text-stone-600">AI reasoning: </span>{active.ai_reason}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="mt-4 text-xs text-stone-400">
        {Object.entries(counts).filter(([k]) => k !== "all").map(([k, v]) => (
          <span key={k} className="mr-3">{k}: {v}</span>
        ))}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
